<script setup>
import { ref, computed, onMounted } from 'vue'
import Textarea from 'primevue/textarea'
import AppDialog from '@/components/ui/AppDialog.vue'
import api from '@/api'
import { useUserStore } from '@/stores/user'
import { useNotify } from '@/services/useNotify'
import { useI18n } from '@/i18n'
import { useConfirmDelete } from '@/composables/useConfirmDelete'

const user = useUserStore()
const notify = useNotify()
const { t } = useI18n()
const { confirmDelete } = useConfirmDelete()

const items = ref([])
const loading = ref(false)

const showDialog = ref(false)
const isAdding = ref(false)
const saving = ref(false)
const editingTag = ref({ name: '', description: '' })

const columns = [
  { field: 'id',          header: t('siem.tags.colId'),          sortable: true,  default: false },
  { field: 'name',        header: t('siem.tags.colName'),        sortable: true,  default: true  },
  { field: 'description', header: t('siem.tags.colDescription'), sortable: false, default: true  },
]

const fetchTags = async () => {
  loading.value = true
  try {
    const { data } = await api.getTags()
    items.value = Array.isArray(data) ? data : (data?.tags ?? [])
  } catch (e) {
    notify.error(t('common.error'), e.response?.data?.message || t('siem.tags.loadFailed'))
  } finally {
    loading.value = false
  }
}

onMounted(fetchTags)

const openCreate = () => {
  isAdding.value = true
  editingTag.value = { name: '', description: '' }
  showDialog.value = true
}
const openEdit = (row) => {
  isAdding.value = false
  editingTag.value = { id: row.id, name: row.name ?? '', description: row.description ?? '' }
  showDialog.value = true
}
const closeDialog = () => { showDialog.value = false }

const saveTag = async () => {
  const { id, ...payload } = editingTag.value
  if (!payload.name?.trim()) { notify.warn(t('common.warning'), t('siem.tags.nameMissing')); return }
  saving.value = true
  try {
    if (isAdding.value) {
      await api.createTag(payload)
      notify.success(t('siem.tags.saved'), t('siem.tags.tagCreated'))
    } else {
      await api.updateTag(id, payload)
      notify.success(t('siem.tags.saved'), t('siem.tags.tagUpdated'))
    }
    showDialog.value = false
    await fetchTags()
  } catch (e) {
    notify.error(t('siem.tags.saveFailed'), e.response?.data?.message || e.message)
  } finally {
    saving.value = false
  }
}

const deleteTags = async (rows) => {
  if (!await confirmDelete(t('siem.tags.confirmDelete'))) return
  try {
    await Promise.all(rows.map(r => api.deleteTag(r.id)))
    notify.success(t('siem.tags.deleted'), t('siem.tags.tagDeleted'))
    await fetchTags()
  } catch (e) {
    notify.error(t('siem.tags.deleteFailed'), e.response?.data?.message || e.message)
  }
}

const tableActions = computed(() => [
  user.can('siem_tags.change') && {
    label:          t('siem.tags.editTooltip'),
    icon:           'pi pi-file-edit',
    requiresSingle: true,
    command:        (rows) => openEdit(rows[0]),
  },
  user.can('siem_tags.delete') && {
    label:     t('siem.tags.deleteTooltip'),
    icon:      'pi pi-trash',
    severity:  'danger',
    separator: true,
    command:   deleteTags,
  },
].filter(Boolean))
</script>

<template>
  <div>
    <AppTable
      table-key="siem-tags"
      :columns="columns"
      :data="items"
      :loading="loading"
      :actions="tableActions"
      @refresh="fetchTags"
    >
      <template #toolbar>
        <pButton
          v-if="user.can('siem_tags.create')"
          icon="pi pi-plus"
          :label="t('siem.tags.addBtn')"
          size="small"
          @click="openCreate"
        />
      </template>
    </AppTable>

    <AppDialog
      v-model="showDialog"
      :title="isAdding ? t('siem.tags.dialogCreate') : t('siem.tags.dialogEdit')"
      size="md"
      :showFooter="false"
      :maximizable="false"
    >
      <form class="grid" @submit.prevent="saveTag">
        <div class="col-12">
          <label class="block mb-2">{{ t('siem.tags.formName') }}</label>
          <InputText v-model.trim="editingTag.name" class="w-full" required />
        </div>
        <div class="col-12">
          <label class="block mb-2">{{ t('siem.tags.formDescription') }}</label>
          <Textarea v-model="editingTag.description" rows="6" class="w-full" />
        </div>
        <div class="col-12 flex justify-end gap-2 mt-2">
          <pButton type="button" :label="t('common.cancel')" severity="secondary" @click="closeDialog" />
          <pButton type="submit" :label="t('common.save')" :loading="saving" />
        </div>
      </form>
    </AppDialog>
  </div>
</template>
